/*!
 * rr-search.js: behaviour layer for the search chrome
 * (Raptor Racing brand spec section 6A "Search (desktop and mobile)",
 * owner-approved v2.12; references search-and-announcement-concepts.html and
 * mobile-search-preview.html; theme v6.8 / v2.13 item 3, 27 Sept 2026).
 *
 * Loaded from layout/theme.liquid with defer, after rr-finder.js. ES5, no
 * dependencies (jQuery is on the page for the theme, it is not used here).
 * Styling: assets/rr-search.css (chrome) and assets/xcloud-instant-search.css
 * (XCloud's rows). Markup: sections/header-search-focus.liquid (desktop
 * field + #rr-search-panel + scrim), snippets/mobile-header.liquid (pill),
 * snippets/mobile-search.liquid (phone sheet #rr-msearch).
 *
 * XCloud Instant Search stays the ONE engine for the dropdown and for the
 * results page (/a/search). This file never fetches results and never
 * touches XCloud's settings. It builds the chrome around XCloud's panel:
 *
 *  (a) Typing placeholder: the overlay span.rr-search__ph types the example
 *      searches from data-rr-examples (85ms per letter, 35ms deleting, 1600ms
 *      hold, Raptor Green caret). Stops while the field is focused or has a
 *      value. Under prefers-reduced-motion, or when data-rr-typing is false,
 *      the static text goes into the input's own placeholder attribute and
 *      the overlay is removed.
 *  (b) Desktop panel: focus with an empty field opens #rr-search-panel (recent
 *      searches, top categories, popular chips, trending brands) and the
 *      scrim; a non-empty value hides the panel (XCloud's dropdown takes
 *      over) and keeps the scrim; Escape, an outside mousedown or focus
 *      leaving the search close everything.
 *  (c) XCloud panel: a MutationObserver on document.body sees every new
 *      #cloud-search-results (XCloud re-creates it per query). Each one is
 *      marked data-rr-done, then: the exact-part pin (query matching
 *      /^[A-Za-z]{1,4}\d[\w.\-]{3,}$/ against the rows' data-sku tokens,
 *      vendor prefix ignored), the Sale label on rows with a compare price
 *      (skipped for trivial sales under $1 or 1 percent, as the cards do),
 *      "See all results for" in the footer, loader off, no-results block
 *      off, and on phones the panel is moved into the sheet.
 *  (d) Claw loader: .is-loading on the active search button 150ms after each
 *      keystroke, cleared by the next panel or after 3s.
 *  (e) No results: 1200ms after the last keystroke of a query of 3+
 *      characters, if no #cloud-search-results exists and the field is still
 *      non-empty, the empty block shows with the query, the request link
 *      (?part=) and the Text us link; hidden by the next panel or an empty
 *      field.
 *  (f) Recent searches: localStorage "rr-recent", max 5, de-duplicated,
 *      pushed on form submit, Enter, the see-more link and product rows;
 *      rendered into the panel and the sheet with remove buttons.
 *  (g) Chips: set the value, focus the field and dispatch input + keyup so
 *      XCloud runs the query.
 *  (i) Phone sheet: opened from the pill and the header magnifier (capture
 *      phase, so z__jsHeader's fancybox popup never opens on phones); slides
 *      in, pushes a history state so browser back closes it, locks body
 *      scroll and restores the scroll position; the pinned See all results
 *      submits the form (XCloud rewrites the action, this file does not
 *      fight it); the clear button empties and re-renders.
 *  (j) Contact page: with ?part= in the URL, the first empty textarea or text
 *      input in the Gorgias or theme contact form is prefilled with
 *      "Part request: <query>" (polled for up to 10s).
 * Boots on DOMContentLoaded and again on shopify:section:load. Everything
 * runs inside try/catch and every query is guarded.
 */
(function () {
  'use strict';

  var doc = document;
  var win = window;

  var RECENT_KEY = 'rr-recent';
  var RECENT_MAX = 5;
  var TYPE_MS = 85;
  var DELETE_MS = 35;
  var HOLD_MS = 1600;
  var IDLE_MS = 400;
  var LOADER_DELAY_MS = 150;
  var LOADER_MAX_MS = 3000;
  var EMPTY_DELAY_MS = 1200;
  var EMPTY_MIN_CHARS = 3;
  /* XCloud down switch (13 Oct 2026, owner; theme setting rr_xcloud_down): layout/theme.liquid blocks XCloud's
     scripts and sets RR_XCLOUD_DOWN. The dropdown then shows Shopify's own results (/search/suggest.json) and
     Enter opens the theme's /search page. */
  var DOWN = !!win.RR_XCLOUD_DOWN;
  var NATIVE_DELAY_MS = 220;
  var NATIVE_MIN_CHARS = 2;
  var nativeCache = {};
  var PANEL_OUT_MS = 250;
  var SHEET_MS = 320;
  var SHEET_FOCUS_MS = 330;
  var CONTACT_POLL_MS = 250;
  var CONTACT_WAIT_MS = 10000;
  var STATIC_FALLBACK = 'Search by part, make, model or part number';
  var EXAMPLES_FALLBACK = ['Hawk HPS 5.0', 'WRX downpipe', 'HB582B.660', 'GR86 coilovers'];

  var motionQuery = win.matchMedia ? win.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var phoneQuery = win.matchMedia ? win.matchMedia('(max-width: 798px)') : null;
  var coarseQuery = win.matchMedia ? win.matchMedia('(pointer: coarse)') : null;

  var desk = null;
  var mob = null;
  var scrim = null;
  var globalsBound = false;

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */
  function guard(fn) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (e) {
        if (win.console && win.console.warn) win.console.warn('rr-search', e);
      }
    };
  }

  /* XCloud stops focus, input, keydown, keyup and change on the search inputs
     with a capture listener that sits between window and the input (probed on
     the draft 28 Sept 2026), so listeners on the input, its wrapper or the
     document never see those events. Window capture runs before it, so the
     handlers for those types are registered there, filtered to the element. */
  var BLOCKED_TYPES = { focus: 1, input: 1, keydown: 1, keyup: 1, change: 1 };
  function listen(ctx, el, type, fn) {
    if (!el) return;
    if (!BLOCKED_TYPES[type]) {
      el.addEventListener(type, fn);
      return;
    }
    var h = function (e) {
      var t = e.target;
      if (t === el || (t && t.nodeType === 1 && el.contains && el.contains(t))) fn.call(el, e);
    };
    win.addEventListener(type, h, true);
    (ctx.winHandlers = ctx.winHandlers || []).push([type, h]);
  }

  function reduced() {
    return !!(motionQuery && motionQuery.matches);
  }

  /* iPadOS 13+ reports itself as a Mac with touch points (fix 27 Sept 2026) */
  function isIOS() {
    var p = navigator.platform || '';
    return /iPad|iPhone|iPod/.test(p) ||
      (p === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  function isPhone() {
    return !!(phoneQuery && phoneQuery.matches);
  }

  function isTouch() {
    return !!((coarseQuery && coarseQuery.matches) || ('ontouchstart' in win) || (navigator.maxTouchPoints > 0));
  }

  function each(list, fn) {
    if (!list) return;
    Array.prototype.forEach.call(list, fn);
  }

  function q(sel, root) {
    return (root || doc).querySelector(sel);
  }

  function qa(sel, root) {
    return (root || doc).querySelectorAll(sel);
  }

  function matches(el, sel) {
    var m = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
    return !!(m && m.call(el, sel));
  }

  function closest(el, sel) {
    while (el && el.nodeType === 1) {
      if (matches(el, sel)) return el;
      el = el.parentNode;
    }
    return null;
  }

  function raf(fn) {
    if (win.requestAnimationFrame) return win.requestAnimationFrame(fn);
    return setTimeout(fn, 16);
  }

  /* input/change as plain events; keyup as a KeyboardEvent so a listener
     that reads e.key does not throw */
  function fire(el, type) {
    var ev = null;
    if (type === 'keyup' || type === 'keydown') {
      try {
        ev = new KeyboardEvent(type, { bubbles: true, cancelable: true, key: 'Unidentified' });
      } catch (e1) {
        ev = null;
      }
    }
    if (!ev) {
      try {
        ev = new Event(type, { bubbles: true, cancelable: true });
      } catch (e2) {
        ev = doc.createEvent('Event');
        ev.initEvent(type, true, true);
      }
    }
    el.dispatchEvent(ev);
  }

  function readList(el, attr, fallback) {
    try {
      var raw = el.getAttribute(attr);
      var arr = raw ? JSON.parse(raw) : null;
      if (Object.prototype.toString.call(arr) !== '[object Array]') return fallback;
      var out = [];
      each(arr, function (v) {
        if (typeof v === 'string' && v.replace(/\s+/g, '')) out.push(v);
      });
      return out.length ? out : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function money(text) {
    var n = parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return isFinite(n) ? n : null;
  }

  function endsWith(s, suffix) {
    return suffix.length <= s.length && s.lastIndexOf(suffix) === s.length - suffix.length;
  }

  function empty(el) {
    while (el && el.firstChild) el.removeChild(el.firstChild);
  }

  function svgClock() {
    var s = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('width', '16');
    s.setAttribute('height', '16');
    s.setAttribute('viewBox', '0 0 24 24');
    s.setAttribute('fill', 'none');
    s.setAttribute('stroke', 'currentColor');
    s.setAttribute('stroke-width', '2');
    s.setAttribute('stroke-linecap', 'round');
    s.setAttribute('aria-hidden', 'true');
    var c = doc.createElementNS('http://www.w3.org/2000/svg', 'circle');
    c.setAttribute('cx', '12');
    c.setAttribute('cy', '12');
    c.setAttribute('r', '9');
    var p = doc.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', 'M12 7v5l3 2');
    s.appendChild(c);
    s.appendChild(p);
    return s;
  }

  function xPanel() {
    return doc.getElementById('cloud-search-results');
  }

  /* ------------------------------------------------------------------------
     Recent searches (f)
     ------------------------------------------------------------------------ */
  function readRecent() {
    try {
      var arr = JSON.parse(win.localStorage.getItem(RECENT_KEY) || '[]');
      if (Object.prototype.toString.call(arr) !== '[object Array]') return [];
      var out = [];
      each(arr, function (v) {
        if (typeof v === 'string' && v.replace(/\s+/g, '') && out.length < RECENT_MAX) out.push(v);
      });
      return out;
    } catch (e) {
      return [];
    }
  }

  function writeRecent(arr) {
    try {
      win.localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, RECENT_MAX)));
    } catch (e) {
      /* private mode or storage blocked: recent searches simply do not persist */
    }
  }

  function pushRecent(term) {
    term = String(term || '').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
    if (!term) return;
    var lower = term.toLowerCase();
    var next = [term];
    each(readRecent(), function (t) {
      if (t.toLowerCase() !== lower) next.push(t);
    });
    writeRecent(next);
    renderRecent();
  }

  function removeRecent(term) {
    var lower = String(term || '').toLowerCase();
    var next = [];
    each(readRecent(), function (t) {
      if (t.toLowerCase() !== lower) next.push(t);
    });
    writeRecent(next);
    renderRecent();
  }

  function clearRecent() {
    writeRecent([]);
    renderRecent();
  }

  function renderRecentInto(ctx) {
    if (!ctx || !ctx.recentList) return;
    var list = readRecent();
    empty(ctx.recentList);
    if (ctx.recentWrap) ctx.recentWrap.hidden = !list.length;
    each(list, function (term) {
      var li = doc.createElement('li');
      var btn = doc.createElement('button');
      btn.type = 'button';
      btn.className = 'rr-search__sug';
      btn.setAttribute('data-rr-term', term);
      btn.appendChild(svgClock());
      var span = doc.createElement('span');
      span.textContent = term;
      btn.appendChild(span);
      var x = doc.createElement('button');
      x.type = 'button';
      x.className = 'rr-search__x';
      x.setAttribute('data-rr-remove', term);
      x.setAttribute('aria-label', 'Remove ' + term + ' from recent searches');
      x.textContent = '✕';
      li.appendChild(btn);
      li.appendChild(x);
      ctx.recentList.appendChild(li);
    });
  }

  function renderRecent() {
    renderRecentInto(desk);
    renderRecentInto(mob);
  }

  /* ------------------------------------------------------------------------
     Typing placeholder (a)
     ------------------------------------------------------------------------ */
  function staticPlaceholder(ctx) {
    if (ctx.ph && ctx.ph.parentNode) ctx.ph.parentNode.removeChild(ctx.ph);
    ctx.ph = null;
    if (!ctx.input.getAttribute('placeholder')) ctx.input.setAttribute('placeholder', ctx.staticText);
  }

  function syncPh(ctx) {
    if (!ctx.ph) return;
    if (ctx.input.value) {
      ctx.ph.textContent = '';
    } else if (doc.activeElement === ctx.input || !ctx.typingOn) {
      ctx.ph.textContent = ctx.staticText;
    }
  }

  function startTyping(ctx) {
    if (!ctx.ph) return;
    if (reduced() || !ctx.typing || !ctx.examples.length) {
      staticPlaceholder(ctx);
      return;
    }
    if (ctx.typingOn) return;
    /* one pass per page view, then the static hint (v7.1 motion review F2:
       nothing loops except loaders) */
    if (ctx.typedOnce) {
      syncPh(ctx);
      return;
    }
    ctx.typingOn = true;
    var ti = 0;
    var ci = 0;
    var deleting = false;

    function step() {
      clearTimeout(ctx.typeTimer);
      if (!ctx.typingOn || !ctx.ph) return;
      if (reduced()) {
        ctx.typingOn = false;
        staticPlaceholder(ctx);
        return;
      }
      /* the desktop field is display: none on phones and the sheet's field
         only matters while the sheet is open: idle cheaply until then */
      if ((ctx.kind === 'desk' && isPhone()) || (ctx.kind === 'mob' && !ctx.isOpen)) {
        ctx.typeTimer = setTimeout(step, 1000);
        return;
      }
      if (ctx.input.value || doc.activeElement === ctx.input) {
        ctx.ph.textContent = ctx.input.value ? '' : ctx.staticText;
        ctx.typeTimer = setTimeout(step, IDLE_MS);
        return;
      }
      var s = ctx.examples[ti];
      if (!deleting) {
        ci++;
        if (ci > s.length) {
          deleting = true;
          ctx.typeTimer = setTimeout(step, HOLD_MS);
          return;
        }
      } else {
        ci--;
        if (ci < 0) {
          deleting = false;
          ci = 0;
          ti = (ti + 1) % ctx.examples.length;
          if (ti === 0) {
            ctx.typingOn = false;
            ctx.typedOnce = true;
            ctx.ph.textContent = ctx.staticText;
            return;
          }
        }
      }
      empty(ctx.ph);
      ctx.ph.appendChild(doc.createTextNode(ctx.lead + '“' + s.slice(0, ci)));
      var cur = doc.createElement('span');
      cur.className = 'rr-search__cur';
      ctx.ph.appendChild(cur);
      ctx.ph.appendChild(doc.createTextNode('”'));
      ctx.typeTimer = setTimeout(step, deleting ? DELETE_MS : TYPE_MS);
    }

    step();
  }

  /* ------------------------------------------------------------------------
     Loader (d) and no-results (e)
     ------------------------------------------------------------------------ */
  function armLoader(ctx) {
    clearTimeout(ctx.loadTimer);
    clearTimeout(ctx.loadMax);
    ctx.loadTimer = setTimeout(function () {
      if (!ctx.input.value) return;
      ctx.button.classList.add('is-loading');
      ctx.loadMax = setTimeout(function () {
        stopLoader(ctx);
      }, LOADER_MAX_MS);
    }, LOADER_DELAY_MS);
  }

  function stopLoader(ctx) {
    clearTimeout(ctx.loadTimer);
    clearTimeout(ctx.loadMax);
    ctx.button.classList.remove('is-loading');
  }

  /* assets/xcloud-instant-search.js publishes its measurement under the
     theme's one search namespace, window.rrSearch (fix 27 Sept 2026) */
  function xcloudSync() {
    var hook = win.rrSearch && win.rrSearch.xcloudSync;
    if (typeof hook === 'function') hook();
  }

  /* Stop every timer of a context whose markup has been replaced (the theme
     editor re-renders the header section on shopify:section:load), so the
     typing loop and any pending panel, loader or no-results timers never run
     on detached nodes (fix 27 Sept 2026). */
  function retire(ctx) {
    if (!ctx) return;
    if (ctx.winHandlers) {
      each(ctx.winHandlers, function (pair) { win.removeEventListener(pair[0], pair[1], true); });
      ctx.winHandlers = [];
    }
    ctx.typingOn = false;
    clearTimeout(ctx.typeTimer);
    clearTimeout(ctx.panelTimer);
    clearTimeout(ctx.emptyTimer);
    clearTimeout(ctx.sheetTimer);
    stopLoader(ctx);
  }

  function requestHref(ctx, query) {
    var base = ctx.requestUrl || '/pages/contact';
    return base + (base.indexOf('?') > -1 ? '&' : '?') + 'part=' + encodeURIComponent(query);
  }

  function smsHref(el) {
    var sms = el.getAttribute('data-rr-sms-href');
    var contact = el.getAttribute('data-rr-contact-href') || '/pages/contact';
    return (isPhone() || isTouch()) && sms ? sms : contact;
  }

  /* the Text us button sends a text on phones and touch devices; on computers, where an sms: link often does
     nothing, it opens the contact form, so it says "Contact us" there (owner, 13 Oct 2026) */
  function setSms(el) {
    /* only the buttons: the search wrappers also carry data-rr-sms (the number) and must never be rewritten */
    if (!el.hasAttribute('data-rr-sms-href')) return;
    var href = smsHref(el);
    if (!el.hasAttribute('data-rr-sms-label')) el.setAttribute('data-rr-sms-label', el.textContent);
    el.setAttribute('href', href);
    el.textContent = /^sms:/.test(href) ? el.getAttribute('data-rr-sms-label') : 'Contact us';
  }

  function fillEmpty(ctx, query) {
    each(qa('[data-rr-q]', ctx.empty), function (el) {
      el.textContent = query;
    });
    each(qa('[data-rr-request]', ctx.empty), function (el) {
      el.setAttribute('href', requestHref(ctx, query));
    });
    each(qa('[data-rr-sms]', ctx.empty), function (el) {
      setSms(el);
    });
  }

  function showEmpty(ctx, query) {
    stopLoader(ctx); /* the claw must not keep running under a no-match block (fix 27 Sept 2026) */
    fillEmpty(ctx, query);
    ctx.empty.hidden = false;
    if (ctx.kind === 'desk') {
      openPanel(ctx, 'empty');
    } else {
      ctx.root.setAttribute('data-rr-mode', 'empty');
      if (ctx.idle) ctx.idle.hidden = true;
    }
  }

  function hideEmpty(ctx) {
    clearTimeout(ctx.emptyTimer);
    if (!ctx.empty.hidden) {
      ctx.empty.hidden = true;
      if (ctx.kind === 'desk') {
        if (ctx.panel.getAttribute('data-rr-mode') === 'empty') closePanel(ctx);
      }
    }
    if (ctx.kind === 'mob' && ctx.root.getAttribute('data-rr-mode') === 'empty') {
      ctx.root.setAttribute('data-rr-mode', ctx.input.value ? 'results' : 'idle');
    }
  }

  function armEmpty(ctx) {
    clearTimeout(ctx.emptyTimer);
    var val = ctx.input.value.replace(/^\s+|\s+$/g, '');
    if (val.length < EMPTY_MIN_CHARS) return;
    ctx.emptyTimer = setTimeout(function () {
      var now = ctx.input.value.replace(/^\s+|\s+$/g, '');
      if (!now || now !== val) return;
      if (xPanel()) return;
      showEmpty(ctx, now);
    }, EMPTY_DELAY_MS);
  }

  /* ------------------------------------------------------------------------
     XCloud down: Shopify's own results in the dropdown
     ------------------------------------------------------------------------ */
  function nativeMoney(v) {
    var n = Number(v);
    if (!isFinite(n)) return '';
    try { return '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
    catch (e) { return '$' + n.toFixed(2); }
  }

  function nativeBox(ctx) {
    if (ctx.native && ctx.native.parentNode) return ctx.native;
    var box = doc.createElement('div');
    box.className = 'rr-native';
    box.setAttribute('data-rr-native', '');
    box.hidden = true;
    (ctx.kind === 'desk' ? ctx.panel : (ctx.results || ctx.root)).appendChild(box);
    ctx.native = box;
    return box;
  }

  function hideNative(ctx) {
    clearTimeout(ctx.nativeTimer);
    if (ctx.native) ctx.native.hidden = true;
  }

  function renderNative(ctx, query, products) {
    var box = nativeBox(ctx);
    box.innerHTML = '';
    var list = doc.createElement('ul');
    list.className = 'rr-native__list';
    each(products, function (p) {
      var li = doc.createElement('li');
      li.className = 'rr-native__item';
      var a = doc.createElement('a');
      a.className = 'rr-native__row';
      a.href = p.url;
      a.addEventListener('click', function () { pushRecent(query); });
      if (p.image) {
        var img = doc.createElement('img');
        img.className = 'rr-native__img';
        img.src = p.image + (p.image.indexOf('?') > -1 ? '&' : '?') + 'width=96';
        img.alt = '';
        img.width = 48;
        img.height = 48;
        a.appendChild(img);
      }
      var title = doc.createElement('span');
      title.className = 'rr-native__t';
      title.textContent = p.title;
      a.appendChild(title);
      var price = Number(p.price), cap = Number(p.compare_at_price_max || 0);
      /* the trivial-sale gate everywhere else uses: at least $1 and a floored 1% */
      var sale = cap > price && cap - price >= 1 && Math.floor(((cap - price) * 100) / cap) >= 1;
      var pr = doc.createElement('span');
      pr.className = 'rr-native__p' + (sale ? ' is-sale' : '');
      pr.textContent = nativeMoney(price);
      if (sale) {
        var was = doc.createElement('s');
        was.textContent = nativeMoney(cap);
        pr.appendChild(was);
      }
      a.appendChild(pr);
      li.appendChild(a);
      list.appendChild(li);
    });
    box.appendChild(list);
    var all = doc.createElement('a');
    all.className = 'rr-native__all';
    all.href = '/search?type=product&q=' + encodeURIComponent(query);
    all.textContent = 'See all results for \u201c' + query + '\u201d';
    all.addEventListener('click', function () { pushRecent(query); });
    box.appendChild(all);
    box.hidden = false;
    if (ctx.kind === 'desk') {
      openPanel(ctx, 'native');
    } else {
      ctx.root.setAttribute('data-rr-mode', 'results');
    }
  }

  function armNative(ctx) {
    clearTimeout(ctx.nativeTimer);
    var val = ctx.input.value.replace(/^\s+|\s+$/g, '');
    if (val.length < NATIVE_MIN_CHARS) {
      hideNative(ctx);
      return;
    }
    ctx.nativeTimer = setTimeout(function () {
      var key = val.toLowerCase();
      var done = function (products) {
        if (ctx.input.value.replace(/^\s+|\s+$/g, '') !== val) return;
        stopLoader(ctx);
        if (!products.length) {
          hideNative(ctx);
          if (val.length >= EMPTY_MIN_CHARS) showEmpty(ctx, val);
          return;
        }
        hideEmpty(ctx);
        renderNative(ctx, val, products);
      };
      if (nativeCache[key]) {
        done(nativeCache[key]);
        return;
      }
      if (!win.fetch) return;
      win.fetch('/search/suggest.json?q=' + encodeURIComponent(val) + '&resources%5Btype%5D=product&resources%5Blimit%5D=8&resources%5Boptions%5D%5Bunavailable_products%5D=last', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          var ps = [];
          try { ps = (d && d.resources.results.products) || []; } catch (e) { ps = []; }
          nativeCache[key] = ps;
          done(ps);
        })
        .catch(function () { done([]); });
    }, NATIVE_DELAY_MS);
  }

  /* ------------------------------------------------------------------------
     Desktop panel and scrim (b)
     ------------------------------------------------------------------------ */
  function setScrim(on) {
    if (!scrim) return;
    if (on) {
      /* Unhide only when hidden, but (re)add the class whenever it is
         missing: a setScrim(false) within the last 250ms has removed .is-on
         while its timer has not yet set hidden, and the old guard left the
         scrim transparent for the rest of the session (fix 27 Sept 2026). */
      clearTimeout(scrim._t);
      if (scrim.hidden) {
        scrim.hidden = false;
        if (!reduced()) void scrim.offsetWidth;
      }
      if (!scrim.classList.contains('is-on')) {
        if (reduced()) {
          scrim.classList.add('is-on');
        } else {
          raf(function () {
            if (!scrim.hidden) scrim.classList.add('is-on');
          });
        }
      }
    } else {
      scrim.classList.remove('is-on');
      clearTimeout(scrim._t);
      scrim._t = setTimeout(function () {
        scrim.hidden = true;
      }, reduced() ? 0 : PANEL_OUT_MS);
    }
  }

  function syncOpen(ctx) {
    if (!ctx || ctx.kind !== 'desk') return;
    var panelShown = !ctx.panel.hidden;
    var x = xPanel();
    var xShown = !!x && !isPhone();
    var open = ctx.active && (panelShown || xShown);
    ctx.wrap.classList.toggle('is-open', open);
    setScrim(ctx.active);
  }

  function nextTabbableAfter(box) {
    var all = qa('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (box.contains(el)) continue;
      if (!(box.compareDocumentPosition(el) & 4)) continue;   /* only what follows the search */
      if (el.getAttribute('tabindex') === '-1' || !el.offsetParent) continue;
      return el;
    }
    return null;
  }

  function openPanel(ctx, mode) {
    clearTimeout(ctx.panelTimer);
    ctx.panel.setAttribute('data-rr-mode', mode || 'idle');
    /* Same shape as setScrim: closePanel removes .is-open at once and sets
       hidden 250ms later, so a re-open inside that window (clearing the
       field after the no-match block, a one-character backspace, Esc then a
       click back in) must add the class even though hidden is already
       false, or the panel stays at opacity 0 (fix 27 Sept 2026). */
    if (ctx.panel.hidden) {
      ctx.panel.hidden = false;
      if (!reduced()) void ctx.panel.offsetWidth;
    }
    if (!ctx.panel.classList.contains('is-open')) {
      if (reduced()) {
        ctx.panel.classList.add('is-open');
      } else {
        raf(function () {
          if (!ctx.panel.hidden) ctx.panel.classList.add('is-open');
        });
      }
    }
    ctx.input.setAttribute('aria-expanded', 'true');
    syncOpen(ctx);
  }

  function closePanel(ctx) {
    clearTimeout(ctx.panelTimer);
    ctx.panel.classList.remove('is-open');
    ctx.input.setAttribute('aria-expanded', 'false');
    var finish = function () {
      ctx.panel.hidden = true;
      syncOpen(ctx);
    };
    if (reduced()) {
      finish();
    } else {
      ctx.panelTimer = setTimeout(finish, PANEL_OUT_MS);
    }
    syncOpen(ctx);
  }

  function closeAll(ctx, blur) {
    if (!ctx) return;
    ctx.active = false;
    hideEmpty(ctx);
    stopLoader(ctx);
    closePanel(ctx);
    ctx.wrap.classList.remove('is-open');
    setScrim(false);
    if (blur && doc.activeElement === ctx.input) ctx.input.blur();
  }

  function onDeskFocus(ctx) {
    ctx.active = true;
    syncPh(ctx);
    if (!ctx.input.value) {
      openPanel(ctx, 'idle');
    } else {
      syncOpen(ctx);
    }
    xcloudSync();
  }

  /* ------------------------------------------------------------------------
     Shared input handling (b, d, e, g)
     ------------------------------------------------------------------------ */
  /* XCloud's phone mode (probed 28 Sept 2026): when one of its inputs gets
     focus on a phone it opens its own fixed top bar (.cloud-search-mobile,
     with its own input and a Cancel link) over a blurred full-screen
     backdrop, and it queries from THAT input only, so typing in the sheet
     returned nothing and the bar covered the sheet's back button.
     rr-search.css hides both while the sheet is open; the sheet's value is
     forwarded into XCloud's input, whose results panel onXPanel then moves
     into the sheet as before. */
  function xMobileInput() {
    return q('.cloud-search-mobile input');
  }

  function forwardToX(ctx) {
    if (!ctx || ctx.kind !== 'mob') return;
    var xi = xMobileInput();
    if (!xi || xi.value === ctx.input.value) return;
    try {
      var desc = Object.getOwnPropertyDescriptor(win.HTMLInputElement.prototype, 'value');
      if (desc && desc.set) desc.set.call(xi, ctx.input.value);
      else xi.value = ctx.input.value;
    } catch (e) {
      xi.value = ctx.input.value;
    }
    fire(xi, 'input');
    fire(xi, 'keyup');
  }

  function resetX() {
    var xi = xMobileInput();
    if (xi && xi.value) {
      xi.value = '';
      fire(xi, 'input');
      fire(xi, 'keyup');
    }
    var cancel = q('.cloud-search-mobile__cancel');
    if (cancel) {
      /* its href is "": never let the click reload the page */
      var stop = function (e) {
        if (e.preventDefault) e.preventDefault();
      };
      cancel.addEventListener('click', stop, false);
      try {
        cancel.click();
      } catch (e) {
        /* XCloud keeps its bar; it is hidden while the sheet is closed anyway */
      }
      cancel.removeEventListener('click', stop, false);
    }
  }

  function onInput(ctx) {
    var val = ctx.input.value;
    syncPh(ctx);
    forwardToX(ctx);
    if (ctx.kind === 'desk') ctx.active = true;
    if (ctx.kind === 'mob') {
      if (ctx.clear) ctx.clear.hidden = !val;
      if (ctx.foot) ctx.foot.hidden = !val;
      if (ctx.idle) ctx.idle.hidden = !!val;
      if (ctx.all) ctx.all.setAttribute('href', ctx.searchUrl + '?q=' + encodeURIComponent(val));
    }
    if (!val) {
      hideEmpty(ctx);
      hideNative(ctx);
      stopLoader(ctx);
      if (ctx.kind === 'desk') {
        if (ctx.active) openPanel(ctx, 'idle');
      } else {
        ctx.root.setAttribute('data-rr-mode', 'idle');
      }
      return;
    }
    hideEmpty(ctx);
    if (ctx.kind === 'desk') {
      /* XCloud down: the native results stay open while typing; the next answer replaces them */
      if (!ctx.panel.hidden && !(DOWN && ctx.panel.getAttribute('data-rr-mode') === 'native')) closePanel(ctx);
      syncOpen(ctx);
    } else {
      ctx.root.setAttribute('data-rr-mode', 'results');
    }
    armLoader(ctx);
    if (DOWN) armNative(ctx);
    else armEmpty(ctx);
  }

  function runQuery(ctx, term) {
    ctx.input.focus();
    ctx.input.value = term;
    if (ctx.kind === 'desk') ctx.active = true;
    fire(ctx.input, 'input');
    fire(ctx.input, 'keyup');
    try {
      ctx.input.setSelectionRange(term.length, term.length);
    } catch (e) {
      /* type=search on some engines refuses selection ranges */
    }
  }

  function onSubmit(ctx, e) {
    var val = ctx.input.value.replace(/^\s+|\s+$/g, '');
    if (!val) {
      if (e && e.preventDefault) e.preventDefault();
      return;
    }
    pushRecent(val);
    /* XCloud down: Enter opens the theme's own search page, never XCloud's /a/search */
    if (DOWN && e) {
      if (e.preventDefault) e.preventDefault();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      win.location.href = '/search?type=product&q=' + encodeURIComponent(val);
    }
  }

  function bindCommon(ctx) {
    listen(ctx, ctx.input, 'input', guard(function () {
      onInput(ctx);
    }));
    listen(ctx, ctx.input, 'keydown', guard(function (e) {
      if (e.key === 'Enter' || e.keyCode === 13) {
        var val = ctx.input.value.replace(/^\s+|\s+$/g, '');
        if (val) pushRecent(val);
      }
      /* v2.18 6L B6: Tab leaves the search for the next part of the page
         (the menu), instead of walking the twenty links in the open panel;
         arrow keys still move through the panel */
      if ((e.key === 'Tab' || e.keyCode === 9) && !e.shiftKey && ctx.kind === 'desk' && ctx.panel && !ctx.panel.hidden) {
        /* 29 Sept audit: the next stop is the field's own Search button (it
           used to jump past it and the account link to the cart) */
        var sb = ctx.form && ctx.form.querySelector('button[type="submit"], .header__search-button');
        var next = (sb && sb.offsetParent) ? sb : nextTabbableAfter(ctx.wrap);
        if (next) {
          e.preventDefault();
          closeAll(ctx, false);
          try { next.focus(); } catch (err) { /* focus is a nicety */ }
        }
      }
    }));
    ctx.form.addEventListener('submit', guard(function (e) {
      onSubmit(ctx, e);
    }));
    /* chips, recent rows, remove buttons, clear all */
    ctx.wrap.addEventListener('click', guard(function (e) {
      var t = e.target;
      var remove = closest(t, '[data-rr-remove]');
      if (remove && ctx.wrap.contains(remove)) {
        e.preventDefault();
        removeRecent(remove.getAttribute('data-rr-remove'));
        ctx.input.focus();
        return;
      }
      var clear = closest(t, '[data-rr-recent-clear]');
      if (clear && ctx.wrap.contains(clear)) {
        e.preventDefault();
        clearRecent();
        return;
      }
      var chip = closest(t, '[data-rr-term]');
      if (chip && ctx.wrap.contains(chip)) {
        e.preventDefault();
        runQuery(ctx, chip.getAttribute('data-rr-term') || '');
      }
    }));
    each(qa('[data-rr-sms]', ctx.wrap), function (el) {
      setSms(el);
    });
  }

  /* ------------------------------------------------------------------------
     Desktop context
     ------------------------------------------------------------------------ */
  function initDesk() {
    var wrap = q('.header__search[data-rr-search]');
    if (!wrap) return;
    if (wrap.getAttribute('data-rr-bound')) {
      if (desk && desk.wrap === wrap) return;
    }
    var form = q('form.header__search-form', wrap);
    var input = form && q('input[name="q"]', form);
    var button = form && q('.header__search-button', form);
    var panel = q('#rr-search-panel', wrap);
    if (!form || !input || !button || !panel) return;
    if (desk && desk.wrap !== wrap) retire(desk);
    wrap.setAttribute('data-rr-bound', '1');

    var ctx = {
      kind: 'desk',
      wrap: wrap,
      form: form,
      input: input,
      button: button,
      panel: panel,
      ph: q('.rr-search__ph', form),
      empty: q('[data-rr-empty]', panel),
      recentWrap: q('[data-rr-recent-wrap]', panel),
      recentList: q('[data-rr-recent]', panel),
      typing: wrap.getAttribute('data-rr-typing') !== 'false',
      examples: readList(wrap, 'data-rr-examples', EXAMPLES_FALLBACK),
      staticText: wrap.getAttribute('data-rr-static') || STATIC_FALLBACK,
      requestUrl: wrap.getAttribute('data-rr-request-url') || '/pages/contact',
      lead: 'Search ',
      active: false
    };
    desk = ctx;

    /* the scrim is printed inside the sticky section (will-change: transform
       would trap a fixed child), so it lives on <body> from here on */
    var printed = q('[data-rr-scrim]');
    if (printed) {
      if (scrim && scrim !== printed && scrim.parentNode) {
        printed.parentNode.removeChild(printed);
      } else {
        scrim = printed;
        if (scrim.parentNode !== doc.body) doc.body.appendChild(scrim);
      }
    }

    bindCommon(ctx);
    listen(ctx, input, 'focus', guard(function () {
      onDeskFocus(ctx);
    }));
    listen(ctx, wrap, 'keydown', guard(function (e) {
      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
        /* 29 Sept audit (spec 6A "Esc closes it"): the XCloud results closed
           too, and focus stays in the field (it used to fall to <body> with
           the results still open) */
        var x = xPanel();
        if (x) x.classList.add('rr-x-hidden');
        closeAll(ctx, false);
      }
    }));
    /* the XCloud results come back with the next keystroke or focus */
    listen(ctx, input, 'input', guard(function () { var x = xPanel(); if (x) x.classList.remove('rr-x-hidden'); }));
    listen(ctx, input, 'mousedown', guard(function () { var x = xPanel(); if (x) x.classList.remove('rr-x-hidden'); }));
    wrap.addEventListener('focusout', guard(function () {
      setTimeout(function () {
        if (!ctx.active) return;
        var a = doc.activeElement;
        if (!a || a === doc.body) return;
        if (wrap.contains(a)) return;
        var x = xPanel();
        if (x && x.contains(a)) return;
        closeAll(ctx, false);
      }, 0);
    }));
    button.addEventListener('click', guard(function () {
      if (!input.value) {
        input.focus();
      }
    }));

    renderRecentInto(ctx);
    startTyping(ctx);
  }

  /* ------------------------------------------------------------------------
     Phone sheet (i)
     ------------------------------------------------------------------------ */
  function lockScroll(ctx) {
    ctx.scrollY = win.pageYOffset || doc.documentElement.scrollTop || 0;
    doc.documentElement.classList.add('rr-msearch-open');
    doc.body.style.position = 'fixed';
    doc.body.style.top = '-' + ctx.scrollY + 'px';
    doc.body.style.width = '100%';
  }

  function unlockScroll(ctx) {
    doc.documentElement.classList.remove('rr-msearch-open');
    doc.body.style.position = '';
    doc.body.style.top = '';
    doc.body.style.width = '';
    win.scrollTo(0, ctx.scrollY || 0);
  }

  function openSheet(ctx, opener) {
    if (!ctx || ctx.isOpen) return;
    ctx.isOpen = true;
    ctx.opener = opener || null;
    clearTimeout(ctx.sheetTimer);
    lockScroll(ctx);
    ctx.root.hidden = false;
    if (reduced()) {
      ctx.root.classList.add('is-open');
    } else {
      void ctx.root.offsetWidth;
      raf(function () {
        if (ctx.isOpen) ctx.root.classList.add('is-open');
      });
    }
    try {
      win.history.pushState({ rrSearch: 1 }, '');
      ctx.pushed = true;
    } catch (e) {
      ctx.pushed = false;
    }
    renderRecentInto(ctx);
    onInput(ctx);
    startTyping(ctx);
    /* iOS Safari raises the keyboard for a programmatic focus() only while
       the user gesture is still on the call stack (openSheet runs
       synchronously from the capture-phase tap), so focus at once there and
       under reduced motion; Android and desktop wait for the slide as the
       theme's own showMobileSearch does (fix 27 Sept 2026). The XCloud
       measurement still runs after the slide, it only reads geometry. */
    var focusInput = function () {
      try {
        ctx.input.focus({ preventScroll: true });
      } catch (e) {
        ctx.input.focus();
      }
    };
    if (isIOS() || reduced()) focusInput();
    setTimeout(function () {
      if (!ctx.isOpen) return;
      if (doc.activeElement !== ctx.input) focusInput();
      xcloudSync();
    }, reduced() ? 0 : SHEET_FOCUS_MS);
  }

  function closeSheet(ctx, fromHistory) {
    if (!ctx || !ctx.isOpen) return;
    ctx.isOpen = false;
    stopLoader(ctx);
    hideEmpty(ctx);
    if (doc.activeElement === ctx.input) ctx.input.blur();
    /* the sheet always reopens in the mock's "Just opened" idle state: a
       stale query left in the field would re-arm the loader and the 1200ms
       no-results timer on the next open although XCloud runs no request
       (fix 27 Sept 2026); the input event resets clear/foot/idle/mode and
       the keyup lets XCloud drop its panel */
    if (ctx.input.value) {
      ctx.input.value = '';
      fire(ctx.input, 'input');
      fire(ctx.input, 'keyup');
    }
    resetX();
    ctx.root.classList.remove('is-open');
    clearTimeout(ctx.sheetTimer);
    var finish = function () {
      ctx.root.hidden = true;
    };
    if (reduced()) finish();
    else ctx.sheetTimer = setTimeout(finish, SHEET_MS);
    unlockScroll(ctx);
    if (!fromHistory && ctx.pushed) {
      ctx.pushed = false;
      try {
        win.history.back();
      } catch (e) {
        /* nothing to pop */
      }
    }
    ctx.pushed = false;
    if (ctx.opener && ctx.opener.focus && doc.body.contains(ctx.opener)) {
      try {
        ctx.opener.focus({ preventScroll: true });
      } catch (e) {
        /* focus is a nicety */
      }
    }
    ctx.opener = null;
  }

  function submitSheet(ctx) {
    var val = ctx.input.value.replace(/^\s+|\s+$/g, '');
    if (!val) {
      ctx.input.focus();
      return;
    }
    pushRecent(val);
    if (ctx.form.requestSubmit) {
      ctx.form.requestSubmit();
    } else {
      ctx.form.submit();
    }
  }

  function initMob() {
    var root = doc.getElementById('rr-msearch');
    if (!root) return;
    if (root.getAttribute('data-rr-bound') && mob && mob.root === root) return;
    var form = q('form', root);
    var input = form && q('input[name="q"]', form);
    var button = form && q('.rr-msearch__go', form);
    if (!form || !input || !button) return;
    if (mob && mob.root !== root) retire(mob);
    root.setAttribute('data-rr-bound', '1');

    var ctx = {
      kind: 'mob',
      root: root,
      wrap: root,
      form: form,
      input: input,
      button: button,
      ph: q('.rr-search__ph', form),
      clear: q('[data-rr-msearch-clear]', root),
      empty: q('[data-rr-empty]', root),
      idle: q('[data-rr-idle]', root),
      results: q('[data-rr-results]', root),
      foot: q('[data-rr-foot]', root),
      all: q('[data-rr-msearch-all]', root),
      recentWrap: q('[data-rr-recent-wrap]', root),
      recentList: q('[data-rr-recent]', root),
      typing: root.getAttribute('data-rr-typing') !== 'false',
      examples: readList(root, 'data-rr-examples', EXAMPLES_FALLBACK),
      staticText: root.getAttribute('data-rr-static') || STATIC_FALLBACK,
      requestUrl: root.getAttribute('data-rr-request-url') || '/pages/contact',
      searchUrl: form.getAttribute('action') || '/search',
      lead: 'Try ',
      isOpen: false,
      pushed: false,
      scrollY: 0
    };
    mob = ctx;

    bindCommon(ctx);
    each(qa('[data-rr-msearch-close]', root), function (el) {
      el.addEventListener('click', guard(function (e) {
        e.preventDefault();
        closeSheet(ctx, false);
      }));
    });
    if (ctx.clear) {
      ctx.clear.addEventListener('click', guard(function (e) {
        e.preventDefault();
        ctx.input.value = '';
        fire(ctx.input, 'input');
        fire(ctx.input, 'keyup');
        ctx.input.focus();
      }));
    }
    if (ctx.all) {
      ctx.all.addEventListener('click', guard(function (e) {
        e.preventDefault();
        submitSheet(ctx);
      }));
    }
    listen(ctx, root, 'keydown', guard(function (e) {
      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
        e.preventDefault();
        closeSheet(ctx, false);
      }
    }));
    if (ctx.ph) ctx.ph.textContent = ctx.staticText;
    if (reduced() || !ctx.typing) staticPlaceholder(ctx);
  }

  /* ------------------------------------------------------------------------
     XCloud panel decoration (c)
     ------------------------------------------------------------------------ */
  function activeCtx() {
    if (mob && mob.isOpen) return mob;
    if (desk && (doc.activeElement === desk.input || desk.active)) return desk;
    if (mob && doc.activeElement === mob.input) return mob;
    return desk || mob;
  }

  /* Exact part number (spec 6A; widened 29 Sept 2026 after the owner found
     it missing): any single word of 4+ characters with a digit counts as a
     part number, so full prefixed SKUs (HAWKHB194F.570) and all-number part
     numbers (90019) qualify, not only the 1 to 4 letters + digit shape.
     XCloud's row carries "SKU  barcode" in data-sku. A row matches when a
     token equals the query (ignoring case and punctuation), or is the query
     behind a supplier prefix of letters only (HAWK + HB194F.570, UMI +
     90019). Exact equals first, then prefixed, up to three rows get the
     "Exact part number" tag and move to the top in that order. */
  /* any single word with a digit: SKUs carry +, #, _ and more (FFTTURRW-88558+42) */
  var PART_RE = /^(?=.*\d)\S{4,}$/;
  function pinExact(panel, query) {
    query = String(query || '').replace(/^\s+|\s+$/g, '');
    if (!PART_RE.test(query)) return;
    var norm = query.toLowerCase();
    var normA = norm.replace(/[^a-z0-9]/g, '');
    if (normA.length < 4) return;
    var hits = [];
    each(qa('a.cs-item', panel), function (row) {
      var title = q('.cs-item-title[data-sku]', row);
      if (!title) return;
      var toks = String(title.getAttribute('data-sku') || '').toLowerCase().split(/\s+/);
      var best = 9;
      for (var i = 0; i < toks.length; i++) {
        var tokA = toks[i].replace(/[^a-z0-9]/g, '');
        if (!tokA) continue;
        if (tokA === normA) { best = 0; break; }
        if (tokA.length > normA.length && endsWith(tokA, normA) && /^[a-z]{1,6}$/.test(tokA.slice(0, tokA.length - normA.length))) best = Math.min(best, 1);
      }
      if (best < 9) hits.push({ row: row, score: best });
    });
    if (!hits.length) return;
    hits.sort(function (x, y) { return x.score - y.score; });
    hits = hits.slice(0, 3);
    /* each hit's unit: the row itself, or its tracking wrapper when XCloud
       wraps each row on its own */
    var units = [];
    for (var u = 0; u < hits.length; u++) {
      var unit = hits[u].row;
      var up = unit.parentNode;
      if (up && matches(up, '[data-product-card-tracked]') && up.children.length === 1) unit = up;
      units.push(unit);
    }
    /* already pinned in score order at the top: change nothing. Moving rows
       that are already in place is a DOM change, which the body observer
       answers with another pass, which moved them again: with 2 or more hits
       that looped forever (9 Oct 2026, a 3-hit "3205" search: about 280 moves
       a second, and each move restarted the rows' fade-in, so they stayed
       near invisible) */
    var inPlace = true;
    for (var k = 0; k < units.length && inPlace; k++) {
      inPlace = !!units[k].parentNode && units[k].parentNode === units[0].parentNode && units[k].parentNode.children[k] === units[k];
    }
    for (var h = hits.length - 1; h >= 0; h--) {
      var hit = hits[h].row;
      var titleEl = q('.cs-item-title', hit);
      if (titleEl && !q('.rr-exact', titleEl)) {
        var tag = doc.createElement('span');
        tag.className = 'rr-exact';
        tag.textContent = 'Exact part number';
        titleEl.insertBefore(tag, titleEl.firstChild);
      }
      if (!hit.classList.contains('rr-exact-row')) hit.classList.add('rr-exact-row');
      /* pin it first; walking the hits backwards leaves them in score order
         at the top */
      var parent = units[h].parentNode;
      if (!inPlace && parent && parent.firstElementChild !== units[h]) {
        parent.insertBefore(units[h], parent.firstChild);
      }
    }
  }

  function decorateSale(panel) {
    each(qa('a.cs-item', panel), function (row) {
      var list = q('.cs-list-price', row);
      if (!list) return;
      var sell = q('.cs-has-list-price', row) || q('.price', row);
      var lp = money(list.textContent);
      var sp = sell ? money(sell.textContent) : null;
      if (lp !== null && sp !== null && (lp - sp < 1 || (lp - sp) / lp < 0.01)) {
        row.classList.add('rr-trivial');
        return;
      }
      var title = q('.cs-item-title', row);
      if (!title || q('.rr-search__lab', row)) return;
      var lab = doc.createElement('span');
      lab.className = 'rr-search__lab';
      lab.textContent = 'Sale';
      var priceBlock = q('.cs-price-block', row);
      if (panel.classList && panel.classList.contains('cloud-search-results--mobile') && priceBlock) {
        /* phone: label leads the price line, as in the approved mobile mock */
        priceBlock.insertBefore(lab, priceBlock.firstChild);
        return;
      }
      var meta = doc.createElement('span');
      meta.className = 'rr-search__meta';
      meta.appendChild(lab);
      title.appendChild(meta);
    });
  }

  function rewriteSeeMore(panel) {
    var link = q('.see-more-results a', panel);
    if (!link) return;
    each(link.childNodes, function (n) {
      if (n.nodeType === 3 && /show all results for/i.test(n.nodeValue)) {
        n.nodeValue = n.nodeValue.replace(/show all results for/i, 'See all results for');
      }
    });
  }

  /* Text suggestions (28 Sept 2026 review: "add text suggestions above
     Collections"). XCloud's API returns suggestions: [] for this store (its
     own suggestions feature is off), so the theme suggests up to four terms
     from what it already knows: the popular searches and trending brand
     chips, the top category names and the visitor's recent searches. A
     block .block-rr-sugg goes above Collections; it is rebuilt only when the
     query changes (data-rr-q), so the panel observer cannot loop on it. */
  var SUGG_MAX = 4;
  var suggPool = null;

  function suggCandidates() {
    var seen = {};
    var out = [];
    var add = function (s) {
      s = String(s || '').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
      var k = s.toLowerCase();
      if (!s || seen[k]) return;
      seen[k] = 1;
      out.push(s);
    };
    each(readRecent(), add);
    if (!suggPool) {
      suggPool = [];
      each(qa('#rr-search-panel [data-rr-term], #rr-msearch [data-rr-term]'), function (el) {
        suggPool.push(el.getAttribute('data-rr-term'));
      });
      each(qa('#rr-search-panel .rr-search__sug, #rr-msearch .rr-msearch__cats a'), function (el) {
        suggPool.push(el.textContent);
      });
    }
    each(suggPool, add);
    return out;
  }

  function suggScore(q, cand) {
    var c = cand.toLowerCase();
    if (c === q) return -1;
    if (c.indexOf(q) === 0) return 0;
    var words = c.split(/[\s,&\/-]+/);
    var qw = q.split(/\s+/);
    var i;
    for (i = 0; i < words.length; i++) {
      if (words[i] && words[i].indexOf(q) === 0) return 1;
    }
    var all = true;
    for (i = 0; i < qw.length && all; i++) {
      var hit = false;
      for (var j = 0; j < words.length; j++) {
        if (qw[i] && words[j].indexOf(qw[i]) === 0) { hit = true; break; }
      }
      all = hit;
    }
    return all && qw.length > 1 ? 2 : -1;
  }

  function suggMark(cand, q) {
    var frag = doc.createDocumentFragment();
    var c = cand.toLowerCase();
    var at = c.indexOf(q);
    if (at > 0 && /[a-z0-9]/.test(c.charAt(at - 1))) {
      var m = new RegExp('(^|[\\s,&/-])' + q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).exec(c);
      at = m ? m.index + m[1].length : -1;
    }
    if (at < 0) {
      frag.appendChild(doc.createTextNode(cand));
      return frag;
    }
    if (at > 0) frag.appendChild(doc.createTextNode(cand.slice(0, at)));
    var hi = doc.createElement('span');
    hi.className = 'highlight-hit';
    hi.textContent = cand.slice(at, at + q.length);   /* the item's own case */
    frag.appendChild(hi);
    if (at + q.length < cand.length) frag.appendChild(doc.createTextNode(cand.slice(at + q.length)));
    return frag;
  }

  /* v2.18 6L B3: real query completions from Shopify's predictive search
     (/search/suggest.json, resources[type]=query), cached per query and
     listed first; the local pool fills in when Shopify has none */
  var remoteSugg = {};
  function fetchRemoteSugg(panel, ql) {
    if (remoteSugg[ql] || ql.length < 2 || !win.fetch) return;
    remoteSugg[ql] = 'loading';
    win.fetch('/search/suggest.json?q=' + encodeURIComponent(ql) + '&resources%5Btype%5D=query&resources%5Blimit%5D=6', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var list = [];
        try { each(d.resources.results.queries || [], function (x) { if (x && x.text) list.push(x.text); }); } catch (e) { list = []; }
        remoteSugg[ql] = list;
        var b = q('.block-rr-sugg', panel);
        if (b) b.removeAttribute('data-rr-q');
        addSuggestions(panel, ql);
      })
      .catch(function () { remoteSugg[ql] = []; });
  }

  function addSuggestions(panel, query) {
    var ql = String(query || '').toLowerCase().replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
    var block = q('.block-rr-sugg', panel);
    if (block && block.getAttribute('data-rr-q') === ql) return;
    var ranked = [];
    fetchRemoteSugg(panel, ql);
    if (ql.length >= 2 && remoteSugg[ql] && remoteSugg[ql] !== 'loading') {
      each(remoteSugg[ql], function (t, n) {
        if (String(t).toLowerCase() !== ql) ranked.push({ c: t, s: -2 + n / 100 });
      });
    }
    if (ql.length >= 2) {
      var have = {};
      each(ranked, function (r) { have[String(r.c).toLowerCase()] = 1; });
      each(suggCandidates(), function (cand) {
        var s = suggScore(ql, cand);
        if (s >= 0 && !have[cand.toLowerCase()]) ranked.push({ c: cand, s: s });
      });
      ranked.sort(function (a, b) { return a.s - b.s || a.c.length - b.c.length; });
    }
    if (!ranked.length) {
      if (block && block.parentNode) block.parentNode.removeChild(block);
      return;
    }
    if (!block) {
      block = doc.createElement('div');
      block.className = 'results-block block-rr-sugg';
      block.setAttribute('role', 'group');
      block.setAttribute('aria-label', 'Suggestions');
    }
    while (block.firstChild) block.removeChild(block.firstChild);
    block.setAttribute('data-rr-q', ql);
    var head = doc.createElement('div');
    head.className = 'header-right';
    head.setAttribute('role', 'heading');
    head.textContent = 'Suggestions';
    block.appendChild(head);
    each(ranked.slice(0, SUGG_MAX), function (r) {
      var btn = doc.createElement('button');
      btn.type = 'button';
      btn.className = 'rr-sugg__item';
      btn.setAttribute('data-rr-sugg', r.c);
      btn.appendChild(suggMark(r.c, ql));
      block.appendChild(btn);
    });
    /* above the products (spec v2.16 6E); on desktop the grid places it at
       the top of the left column, level with Products */
    var anchor = q('.block-products', panel) || q('.block-categories', panel) || q('.block-pages', panel) || q('.see-more-results', panel);
    if (anchor && anchor.parentNode === panel) {
      if (block.nextSibling !== anchor) panel.insertBefore(block, anchor);
    } else if (block.parentNode !== panel) {
      panel.appendChild(block);
    }
  }

  function bindSuggClicks() {
    /* capture on window: XCloud reacts to pointer events inside its panel,
       and a mousedown that blurs the field would close the panel first */
    win.addEventListener('mousedown', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-rr-sugg]') : null;
      if (b) e.preventDefault();
    }, true);
    win.addEventListener('click', guard(function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-rr-sugg]') : null;
      if (!b) return;
      e.preventDefault();
      e.stopPropagation();
      var ctx = activeCtx();
      if (ctx) runQuery(ctx, b.getAttribute('data-rr-sugg'));
    }), true);
  }

  function onXPanel() {
    /* XCloud keeps one #cloud-search-results inside a wrapper and swaps its
       rows on every query (probed 28 Sept 2026), so this runs on each content
       change; every step below is idempotent, so our own edits settle after
       one extra pass */
    var panel = xPanel();
    if (!panel) return;
    var ctx = activeCtx();
    var query = ctx ? ctx.input.value.replace(/^\s+|\s+$/g, '') : '';
    if (ctx) {
      stopLoader(ctx);
      hideEmpty(ctx);
    }
    decorateSale(panel);
    pinExact(panel, query);
    rewriteSeeMore(panel);
    addSuggestions(panel, query);
    if (mob && mob.isOpen && mob.results && isPhone()) {
      if (panel.parentNode !== mob.results) mob.results.appendChild(panel);
      mob.root.setAttribute('data-rr-mode', 'results');
    } else if (desk && ctx === desk) {
      if (!desk.panel.hidden) closePanel(desk);
      syncOpen(desk);
    }
  }

  function watchBody() {
    if (!win.MutationObserver || !doc.body) return;
    var timer = null;
    var mo = new MutationObserver(function (muts) {
      var touched = false;
      var panel = xPanel();
      for (var i = 0; i < muts.length && !touched; i++) {
        var m = muts[i];
        if (panel && (m.target === panel || panel.contains(m.target))) {
          touched = true;
          break;
        }
        var added = m.addedNodes;
        for (var k = 0; k < added.length; k++) {
          var n = added[k];
          if (n.nodeType === 1 && (n.id === 'cloud-search-results' || (n.querySelector && n.querySelector('#cloud-search-results')))) {
            touched = true;
            break;
          }
        }
      }
      if (!touched) return;
      clearTimeout(timer);
      timer = setTimeout(guard(function () {
        onXPanel();
        if (desk) syncOpen(desk);
      }), 30);
    });
    /* subtree: the panel is not a direct child of <body> and is reused */
    mo.observe(doc.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     Document-level bindings (once)
     ------------------------------------------------------------------------ */
  function bindGlobals() {
    if (globalsBound) return;
    globalsBound = true;

    /* phone: magnifier and pill open the sheet; capture phase and
       stopImmediatePropagation keep z__jsHeader's document-delegated click
       (fancybox popup) from ever seeing it */
    doc.addEventListener('click', guard(function (e) {
      var t = closest(e.target, '[data-show-search-trigger], [data-rr-msearch-open]');
      if (!t || !mob) return;
      var isPill = t.hasAttribute('data-rr-msearch-open');
      if (!isPill && !isPhone()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      openSheet(mob, t);
    }), true);
    doc.addEventListener('keydown', guard(function (e) {
      if (!mob || !isPhone()) return;
      if (e.key !== 'Enter' && e.key !== ' ' && e.keyCode !== 13 && e.keyCode !== 32) return;
      var t = closest(e.target, '[data-show-search-trigger]');
      if (!t) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      openSheet(mob, t);
    }), true);

    win.addEventListener('popstate', guard(function () {
      if (mob && mob.isOpen) {
        mob.pushed = false;
        closeSheet(mob, true);
      }
    }));

    /* desktop: mousedown outside the search (and outside XCloud's panel) closes */
    doc.addEventListener('mousedown', guard(function (e) {
      if (!desk || !desk.active) return;
      if (desk.wrap.contains(e.target)) return;
      var x = xPanel();
      if (x && x.contains(e.target)) return;
      closeAll(desk, false);
    }));

    /* recent searches from XCloud's own rows and footer link */
    doc.addEventListener('click', guard(function (e) {
      var x = xPanel();
      if (!x || !x.contains(e.target)) return;
      var hit = closest(e.target, '.see-more-results a, a.cs-item');
      if (!hit) return;
      var ctx = activeCtx();
      if (ctx) pushRecent(ctx.input.value);
    }), true);

    /* the desktop field is hidden on phones and the sheet on desktop: a
       resize across 798px while either is open closes it */
    if (phoneQuery && phoneQuery.addEventListener) {
      phoneQuery.addEventListener('change', guard(function (ev) {
        if (ev.matches && desk) closeAll(desk, true);
        if (!ev.matches && mob && mob.isOpen) closeSheet(mob, false);
      }));
    }

    /* the Text us link outside the search chrome (the /search page block) */
    each(qa('[data-rr-sms]'), function (el) {
      setSms(el);
    });
  }

  /* ------------------------------------------------------------------------
     Contact page prefill (j)
     ------------------------------------------------------------------------ */
  function contactPrefill() {
    if (!/\/pages\/contact\/?$/.test(win.location.pathname)) return;
    var m = /(?:^|[?&])part=([^&]*)/.exec(win.location.search || '');
    if (!m) return;
    var query;
    try {
      query = decodeURIComponent(m[1].replace(/\+/g, ' '));
    } catch (e) {
      query = m[1];
    }
    query = query.replace(/^\s+|\s+$/g, '');
    if (!query) return;
    var text = 'Part request: ' + query;
    var started = Date.now();

    function setValue(el, value) {
      var proto = el.tagName === 'TEXTAREA' ? win.HTMLTextAreaElement : win.HTMLInputElement;
      var desc = proto && Object.getOwnPropertyDescriptor(proto.prototype, 'value');
      if (desc && desc.set) desc.set.call(el, value);
      else el.value = value;
      fire(el, 'input');
      fire(el, 'change');
    }

    function tick() {
      var scopes = qa('[data-gorgias-contact-form-uid], form.custom-contact-form, .gorgias-contact-form, form[action*="/contact"]');
      var target = null;
      each(scopes, function (scope) {
        if (target) return;
        target = q('textarea', scope) || q('input[type="text"]', scope);
      });
      if (target) {
        if (!target.value) setValue(target, text);
        return;
      }
      if (Date.now() - started < CONTACT_WAIT_MS) setTimeout(tick, CONTACT_POLL_MS);
    }

    tick();
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  var boot = guard(function () {
    initMob();
    initDesk();
    bindGlobals();
    watchBody();
    bindSuggClicks();
    renderRecent();
    contactPrefill();
  });

  var rebind = guard(function () {
    initMob();
    initDesk();
    renderRecent();
  });

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders the header section: the field, panel and scrim
     come back as new nodes */
  doc.addEventListener('shopify:section:load', function () {
    setTimeout(rebind, 0);
  });

  /* v2.18 6L B1: the search app's results page (/a/search, where Enter and
     "See all results" go) only said "did not match any results". When it
     shows its no-results line, the branded empty state (the header's own
     template: Request this part with the query filled in, Text us, popular
     categories) goes in its place. */
  (function appNoResults() {
    if (!/^\/a\/search/.test(win.location.pathname)) return;
    var query = '';
    try { query = new URLSearchParams(win.location.search).get('q') || ''; } catch (e) { query = ''; }
    var done = false;
    function build(host) {
      var tpl = doc.querySelector('[data-rr-empty]');
      if (!tpl) return false;
      var box = tpl.cloneNode(true);
      box.removeAttribute('hidden');
      box.removeAttribute('data-rr-empty');
      box.classList.add('rr-search__empty--page', 'rr-search__empty--app');
      var qs = box.querySelector('[data-rr-q]');
      if (qs) qs.textContent = query;
      var req = box.querySelector('[data-rr-request]');
      if (req) req.setAttribute('href', req.getAttribute('href').replace(/part=.*$/, 'part=' + encodeURIComponent(query)));
      var sms = box.querySelector('[data-rr-sms]');
      if (sms && win.matchMedia && win.matchMedia('(max-width: 798px)').matches) sms.setAttribute('href', sms.getAttribute('data-rr-sms-href'));
      host.innerHTML = '';
      host.appendChild(box);
      host.classList.add('rr-app-empty');
      return true;
    }
    /* v2.18 6L D7: a search or filters inside a collection page of the app
       (/a/search/<handle>) that match nothing: the collection empty state */
    function collectionEmpty(host) {
      var params;
      try { params = new URLSearchParams(win.location.search); } catch (e) { params = null; }
      var q = params ? (params.get('filter_search') || params.get('q') || '') : '';
      var h = doc.querySelector('h1');
      var title = h ? (h.textContent || '').replace(/\s+/g, ' ').trim() : '';
      var noun = title ? title.toLowerCase() : 'parts';
      var handle = (win.location.pathname.split('/')[3] || '').replace(/[^\w-]/g, '');
      var box = doc.createElement('div');
      box.className = 'rr-vempty rr-vempty--search';
      var h2 = doc.createElement('h2');
      h2.className = 'rr-vempty__h';
      h2.textContent = 'No ' + noun + ' match' + (q ? ' \u201c' + q + '\u201d' : ' these filters');
      var p = doc.createElement('p');
      p.className = 'rr-vempty__p';
      p.textContent = 'We carry 750,000+ parts, so we may still be able to get it. Try all ' + noun + ', check the spelling or part number, or ask us.';
      var bt = doc.createElement('div');
      bt.className = 'rr-vempty__bt';
      bt.innerHTML = '<a class="button button--primary"><span class="text"></span></a><a class="button button--secondary"><span class="text">Request this part</span></a>';
      var links = bt.querySelectorAll('a');
      links[0].href = handle ? '/collections/' + handle : '/collections/all';
      links[0].querySelector('.text').textContent = 'Show all ' + noun;
      links[1].href = '/pages/contact' + (q ? '?part=' + encodeURIComponent(q) : '');
      box.appendChild(h2);
      box.appendChild(p);
      box.appendChild(bt);
      host.innerHTML = '';
      host.appendChild(box);
      host.classList.remove('text-align-center');
      return true;
    }
    function scan() {
      if (done) return;
      var ch = doc.querySelector('.container--no-products-in-collection');
      if (ch && !ch.querySelector('.rr-vempty')) { done = collectionEmpty(ch); if (done) return; }
      var host = doc.querySelector('.search__no-results');
      if (!host || !/did not match|no results|0 results/i.test(host.textContent || '')) return;
      if (build(host)) {
        done = true;
        [].slice.call(doc.querySelectorAll('h1, h2, h3, h4, p, div')).forEach(function (h) {
          if (h.children.length === 0 && /^\s*0 results\s*$/i.test(h.textContent || '')) h.setAttribute('hidden', '');
        });
      }
    }
    scan();
    if (win.MutationObserver) {
      var mo = new MutationObserver(scan);
      mo.observe(doc.body, { childList: true, subtree: true });
      setTimeout(function () { mo.disconnect(); }, 20000);
    }
  })();
})();
